import Vue from "vue";
import Router from "vue-router";
import store from "./store/store";
import _ from "lodash";
import {auth, db} from "@/firebase/init";
import Login from "./views/Login.vue";
import Signup from "./views/Signup.vue";
import TwoFA from "./views/TwoFA";
import ResetPassword from './views/ResetPassword.vue';

Vue.use(Router);

const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    routes: [
        {
            path: "/",
            name: "login",
            component: Login,
            meta: {
                requiresNotAuth: true
            }
        },
        {
            path: "/reset-password",
            name: "ResetPassword",
            component: ResetPassword,
            meta: {
                requiresNotAuth: true
            }
        },
        {
            path: "/signup/:secret/:secret2",
            name: "signup",
            component: Signup,
            meta: {
                requiresNotAuth: true
            }
        },
        {
            path: "/forgot-password",
            name: "forgotPassword",
            component: () =>
                import(/* webpackChunkName: "forgotPassword" */ "./views/ForgotPassword.vue"),
            meta: {
                requiresNotAuth: true
            }
        },
        {
            path: "/two-fa",
            name: "twoFA",
            component: TwoFA,
            meta: {
                requiresAuth: true,
            }
        },
        {
            path: "/dashboard",
            name: "userDashboard",
            component: () =>
                import(/* webpackChunkName: "userDashboard" */ "./views/user/Dashboard.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            }
        },
        {
            path: "/edit-profile",
            name: "editUserProfile",
            component: () =>
                import(/* webpackChunkName: "userDashboard" */ "./views/user/EditProfile/EditProfile.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            }
        },
        {
            path: "/sites",
            component: () =>
                import(/* webpackChunkName: "sites" */ "./views/user/sites/Sites.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "siteDetails",
                    component: () =>
                        import(/*webpackChunkName: "siteDetails"*/ "./views/user/sites/SiteDetails/SiteDetails.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "add-site",
                    name: "addSite",
                    component: () =>
                        import(/*webpackChunkName: "addSite"*/ "./views/user/sites/AddSite/AddSite.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "server-logs",
                    name: "logs",
                    component: () =>
                        import(/* webpackChunkName: "userDashboard" */ "./views/user/sites/Logs/Logs.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "view-details/:index/:env?",
                    name: "viewDetails",
                    component: () =>
                        import(/*webpackChunkName: "siteDetails"*/ "./views/user/sites/ViewHostingDetails/ViewDetails.vue"),
                    props: true,
                    beforeEnter(to, from, next) {
                        if (!to.params.index) {
                            next({name: "errorPage"});
                        } else {
                            next();
                        }
                    },
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                }
            ]
        },
        {
            path: "/migration",
            component: () =>
                import(/* webpackChunkName: "sites" */ "./views/user/migration/Migration.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "migrationLog",
                    component: () =>
                        import(/*webpackChunkName: "siteDetails"*/ "./views/user/migration/MigrationLog/MigrationLog.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "request-migration",
                    name: "requestMigration",
                    component: () =>
                        import(/*webpackChunkName: "siteDetails"*/ "./views/user/migration/RequestMigration/RequestMigration.vue"),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                }
            ]
        },
        {
            path: "/notifications",
            component: () =>
                import(/* webpackChunkName: "notifications" */ "./views/user/notification/Notifications.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "notificationsPage",
                    component: () =>
                        import(/*webpackChunkName: "NotificationList"*/ "./views/user/notification/list/NotificationList.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                }
            ]
        },
        {
            path: "/tickets",
            component: () =>
                import(/* webpackChunkName: "supportTicketsHolder" */ "./views/user/supportTickets/tickets.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "ticketsArchive",
                    component: () =>
                        import(/*webpackChunkName: "ticketsArchive"*/ "./views/user/supportTickets/archive/TicketArchive.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "open-new",
                    name: "openTicket",
                    component: () =>
                        import(/*webpackChunkName: "function"*/ "./views/user/supportTickets/OpenTicket/OpenTicket.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
                {
                    path: "view/:id",
                    name: "viewTicket",
                    component: () =>
                        import(/*webpackChunkName: "viewTicket"*/ "./views/user/supportTickets/ViewTicket/ViewTicket.vue"),
                    props: true,
                    beforeEnter(to, from, next) {
                        if (!to.params.id) {
                            next({name: "errorPage"});
                        } else {
                            next();
                        }
                    },
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                }
            ]
        },
        {
            path: "/collaborators",
            component: () =>
                import(/* webpackChunkName: "supportTicketsHolder" */ "./views/user/Collaborator/Collaborator.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "Collaborators",
                    component: () =>
                        import(/*webpackChunkName: "ticketsArchive"*/ "./views/user/Collaborator/Collaborator.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
            ]
        },
        {
            path: "/collaborator/sites",
            component: () =>
                import(/* webpackChunkName: "CollaborateSiteList" */ "./views/Layout.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            // beforeEnter(to, from, next) {
            // 	if (!to.params.userId) {
            // 		next({name: "errorPage"});
            // 	} else {
            // 		next();
            // 	}
            // },
            children: [
                {
                    path: "",
                    name: "CollaboratorSites",
                    component: () =>
                        import("./views/user/Collaborator/sites/ViewHostingDetails/ViewDetails.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    },
                },
                /*{
                    path: "add-site",
                    name: "addSite",
                    component: () =>
                        import(/!*webpackChunkName: "addSite"*!/ "./views/user/sites/AddSite/AddSite.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },*/
                /*{
                    path: "view-details/:index",
                    name: "collaboratorViewDetails",
                    component: () =>
                        import( "./views/user/Collaborator/sites/ViewHostingDetails/ViewDetails.vue"),
                    props: true,
                    beforeEnter(to, from, next) {
                        if (!to.params.index) {
                            next({name: "errorPage"});
                        } else {
                            next();
                        }
                    },
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                }*/
            ]
        },
        {
            path: "/admin",
            // route level code-splitting
            // this generates a separate chunk (about.[hash].js) for this route
            // which is lazy-loaded when the route is visited.
            component: () =>
                import(/* webpackChunkName: "admin" */ "./views/admin/Admin.vue"),
            meta: {
                requiresAuth: true,
                requiresAdmin: true,
                permission: [
                    "manager",
                    "admin",
                    "super-admin"
                ]
            },
            children: [
                {
                    path: "/leave-requests",
                    name: "leaveRequests",
                    component: () =>
                        import(/* webpackChunkName: "userDashboard" */ "./views/admin/HrManagement/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                }, {
                    path: "/leaves",
                    name: "myLeaves",
                    component: () =>
                        import(/* webpackChunkName: "userDashboard" */ "./views/admin/LeaveManagement/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "",
                    name: "adminDashboard",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/DashboardUI/index"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "statistics-panel",
                    name: "statisticsPanel",
                    component: () =>
                        import(/* webpackChunkName: "adminDashboard" */ "./views/admin/StatisticsPanel/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                }, {
                    path: "load-stats",
                    name: "loadStats",
                    component: () =>
                        import(/* webpackChunkName: "adminDashboard" */ "./views/admin/ticketLoadStats/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "edit-profile",
                    name: "editAdminProfile",
                    component: () =>
                        import(/* webpackChunkName: "editProfile" */ "./views/admin/EditAdminProfile/EditAdminProfile.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "manager",
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "create-user",
                    name: "createUser",
                    component: () =>
                        import(/* webpackChunkName: "createUser" */ "./views/admin/CreateUser/CreateUserForm.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "client-feedback",
                    name: "clientFeedback",
                    component: () =>
                        import(/* webpackChunkName: "createUser" */ "./views/admin/ClientFeedback/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "user-list",
                    name: "userList",
                    component: () =>
                        import(/* webpackChunkName: "userList" */ "./views/admin/CreatedUserList/CreatedUserList.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "edit-user/:docID",
                    name: "editUser",
                    component: () =>
                        import(/* webpackChunkName: "editUser" */ "./views/admin/EditUser/EditUser.vue"),
                    props: true,
                    beforeEnter(to, from, next) {
                        if (!to.params.docID) {
                            next({name: "errorPage"});
                        } else {
                            next();
                        }
                    },
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "create-admin",
                    name: "createAdminUser",
                    component: () =>
                        import(/* webpackChunkName: "createAdminUser" */ "./views/admin/CreateAdmin/CreateAdmin.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "admins-list",
                    name: "adminsList",
                    component: () =>
                        import(/* webpackChunkName: "adminsList" */ "./views/admin/AdminsList/AdminsList.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "logs",
                    name: "adminLogs",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/LogsList/LogsList.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "outages",
                    name: "outages",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/Outages/index"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                }, {
                    path: "betteruptime",
                    name: "betteruptime",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/BetterUptime/index"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "graph/:id",
                    name: "graph",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/BetterUptime/graph.vue"),
                    props: (route) => ({id: route.params.id}),
                    beforeEnter(to, from, next) {
                        if (!to.params.id) {
                            next({name: "errorPage"});
                        } else {
                            next();
                        }
                    },
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                // {
                // 	path: "announcements",
                // 	name: "announcements",
                // 	component: () =>
                // 		import(/* webpackChunkName: "adminLogs" */ "./views/admin/Announcements/Announcements.vue"),
                // 	meta: {
                // 		requiresAuth: true,
                // 		requiresAdmin: true,
                // 		permission: [
                // 			"admin",
                // 			"manager"
                // 		]
                // 	}
                // },
                {
                    path: "communications",
                    name: "communications",
                    component: () =>
                        import(/* webpackChunkName: "adminLogs" */ "./views/admin/Communications/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "tasks-queue",
                    name: "tasks-queue",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/TasksQueue/Tasks.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "support-tasks",
                    name: "support-tasks",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/TasksQueue/Support.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "migration-tasks",
                    name: "migration-tasks",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Migrations/migrationTasks.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "perf-and-sec-center",
                    name: "cf-enterprise",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/CloudflareEnterprise/cfEntTasks.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "sync-servers",
                    name: "sync-servers",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/DiskUsage/SyncServers.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "cloudflare",
                    name: "cloudflare",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/ManageTenantAccounts/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "mediavine",
                    name: "mediavine",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Mediavine/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "tools",
                    name: "tools",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Tools/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "admin",
                            "manager",
                            "super-admin"
                        ]
                    }
                },
                {
                    path: "lxd",
                    name: "lxd",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Lxd/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "super-admin",
                            "admin",
                            "manager",
                        ]
                    }
                },
                {
                    path: "lxd/create",
                    name: "create-lxd",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Lxd/create.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "super-admin",
                            "admin",
                            "manager",
                        ]
                    }
                },
                {
                    path: "lxd/:id",
                    name: "lxd-details",
                    component: () => import(/* webpackChunkName: "adminLogs" */ "./views/admin/Lxd/details/index.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresAdmin: true,
                        permission: [
                            "super-admin",
                            "admin",
                            "manager",
                        ]
                    }
                },
            ]
        },
        {
            path: "/email",
            component: () =>
                import(/* webpackChunkName: "supportTicketsHolder" */ "./views/user/Email/Email.vue"),
            meta: {
                requiresAuth: true,
                requiresUser: true
            },
            children: [
                {
                    path: "",
                    name: "email",
                    component: () =>
                        import(/*webpackChunkName: "ticketsArchbug fixedive"*/ "./views/user/Email/Email.vue"),
                    meta: {
                        requiresAuth: true,
                        requiresUser: true
                    }
                },
            ]
        },
        {
            path: "/clear-data",
            name: "clearData",
            component: () =>
                import(/* webpackChunkName: "errorPage" */ "./views/clearData.vue")
        },
        {
            path: "*",
            name: "errorPage",
            component: () =>
                import(/* webpackChunkName: "errorPage" */ "./views/404.vue")
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    if (to.matched.some(rec => rec.meta.requiresAuth)) {
        if (auth.currentUser) {
            if (to.matched.some(rec => rec.meta.requiresAdmin)) {
                let userDtls = null;
                if (localStorage.getItem("userDtls")) {
                    userDtls = JSON.parse(localStorage.getItem("userDtls"));
                    store.dispatch('updateUserDetails', userDtls);
                    // Check if the userType is admin then proceed to the admin routes else redirect the user to the user dashboard
                    console.log('User Type: ', userDtls.userType, userDtls);
                    let matched = to.matched[to.matched.length - 1];
                    console.log('matched', matched)
                    let twoFA = userDtls.twoFA;
                    console.log('twoFA', twoFA)
                    if (twoFA === undefined) {
                        const doc = await db.collection("users").doc(userDtls.uid).get();
                        console.log('6666', doc.exists)
                        twoFA = doc.data().twoFA;
                        userDtls.twoFA = twoFA;
                        localStorage.setItem("userDtls", JSON.stringify(userDtls))
                    }
                    console.log('two fa verified')
                    // if(doc.data() && !doc.data().twoFAVerified && doc.data().twoFA && userDtls.userType !== 'normal') {
                    // 	next(false)
                    // } else {
                    if (userDtls.userType !== "normal" && !twoFA && to.path.includes('admin') && !to.path.includes('edit-profile')) {
                        console.log('674')
                        next({name: 'editAdminProfile'})
                    } else if (_.indexOf(matched.meta.permission, userDtls.userType) !== -1) {
                        console.log('677')
                        store.dispatch('updateAdminDetails', {});
                        console.log('User Type: 1', userDtls.userType);
                        next();
                    }
                        // else if (userDtls.userType === "user" || userDtls.userType === "normal") {
                        // 	console.log('682')
                        // 	console.log('User Type: 2', userDtls.userType);
                        // 	next({name: "userDashboard"});
                    // }
                    else {
                        console.log('User Type: 3', userDtls.userType);
                        next({name: "errorPage"});
                    }
                    // }/
                }
            }
                // else if (to.matched.some(rec => rec.meta.requiresUser)) {
                // 	let userDtls = null;
                // 	if (localStorage.getItem("userDtls")) {
                // 		userDtls = JSON.parse(localStorage.getItem("userDtls"));
                // 		const adminDtls = JSON.parse(localStorage.getItem('adminDtls'));
                // 		store.dispatch('updateUserDetails', userDtls);
                // 		store.dispatch('setRequestUserId', userDtls.uid);
                // 		store.dispatch('setRequestCustomerID', userDtls.customerID);
                // 		// Check if the userType is normal then proceed to the user routes else redirect the user to the admin dashboard
                // 		if(localStorage.getItem('adminDtls') && userDtls.userType === "normal"){
                //             store.dispatch('updateAdminDetails', adminDtls);
                //             next()
                //         }else if (userDtls.userType === "normal") {
                //             store.dispatch('updateAdminDetails', {});
                // 			next();
                // 		} else {
                // 			next({name: "adminDashboard"});
                // 		}
                // 	}
            // }
            else {
                next();
            }
            store.dispatch("plan/fetchPlans")
        } else {
            // User is not sign in in, redirect to the login page
            next({name: "login"});
        }
    } else if (to.matched.some(rec => rec.meta.requiresNotAuth)) {
        if (auth.currentUser) {
            let userDtls = null;
            if (localStorage.getItem("userDtls")) {
                userDtls = JSON.parse(localStorage.getItem("userDtls"));
                store.dispatch('updateUserDetails', userDtls);
                // Check if the userType is admin then proceed to the admin routes else redurect the user to the user dashboard
                if (userDtls.userType === "admin" || userDtls.userType === "manager" || userDtls.userType === "super-admin") {
                    next({name: "adminDashboard"});
                } else {
                    next({name: "userDashboard"});
                }
            }
        } else {
            next();
        }
    } else {
        next();
    }
    // Check if the to route requires auth
    // if (to.matched.some(rec => rec.meta.requiresAuth)) {
    // 	// Check auth state of the user
    // 	if (auth.currentUser) {
    // 		// User already signed in
    // 		// Check if the route needs the user to be admin
    // 		if (to.matched.some(rec => rec.meta.requiresAdmin)) {
    // 			// Get the details about the loggedin user from the local storage
    // 			let userDtls = null;
    // 			if (localStorage.getItem("userDtls")) {
    // 				userDtls = JSON.parse(localStorage.getItem("userDtls"));
    // 				// Check if the userType is admin then proceed to the admin routes else redurect the user to the user dashboard
    // 				let matched = to.matched[to.matched.length - 1];
    // 				if(_.indexOf(matched.meta.permission, userDtls.userType) !== -1){
    // 					next();
    // 				} else if (userDtls.userType === "user" ) {
    // 					next({name: "userDashboard"});
    // 				} else {
    // 					next({name: "errorPage"});
    // 				}
    // 			} else {
    // 				db.collection("users")
    // 					.doc(auth.currentUser.uid)
    // 					.get()
    // 					.then(doc => {
    // 						// Check if the document exists - then do more checks; Else throw error
    // 						if (doc.exists) {
    // 							userDtls = {
    // 								firstName: doc.data().firstName,
    // 								lastName: doc.data().lastName,
    // 								email: doc.data().email,
    // 								userType: doc.data().userType,
    // 								customerID: doc.data().customerID
    // 									? doc.data().customerID
    // 									: null,
    // 								uid: doc.id
    // 							};
    // 							// Store the received data about the user in the Local Storage
    // 							localStorage.setItem("userDtls", JSON.stringify(userDtls));
    //
    // 							// Also dispatch the details to Vuex store
    // 							store.dispatch("updateUserDetails", userDtls);
    // 						}
    //
    // 						// Check if the userType is admin then proceed to the admin routes else redurect the user to the user dashboard
    // 						let matched = to.matched[to.matched.length - 1];
    // 						if(_.indexOf(matched.meta.permission, userDtls.userType) !== -1){
    // 							next();
    // 						} else if (userDtls.userType === "user" ) {
    // 							next({name: "userDashboard"});
    // 						} else {
    // 							next({name: "errorPage"});
    // 						}
    // 					});
    // 			}
    // 		} else if (to.matched.some(rec => rec.meta.requiresUser)) {
    // 			// Get the details about the loggedin user from the local storage
    // 			let userDtls = null;
    // 			if (localStorage.getItem("userDtls")) {
    // 				userDtls = JSON.parse(localStorage.getItem("userDtls"));
    // 				// Check if the userType is normal then proceed to the user routes else redurect the user to the admin dashboard
    // 				if (userDtls.userType === "normal") {
    // 					next();
    // 				} else {
    // 					next({name: "adminDashboard"});
    // 				}
    // 			} else {
    // 				db.collection("users")
    // 					.doc(auth.currentUser.uid)
    // 					.get()
    // 					.then(doc => {
    // 						// Check if the document exists - then do more checks; Else throw error
    // 						if (doc.exists) {
    // 							userDtls = {
    // 								firstName: doc.data().firstName,
    // 								lastName: doc.data().lastName,
    // 								email: doc.data().email,
    // 								userType: doc.data().userType,
    // 								customerID: doc.data().customerID
    // 									? doc.data().customerID
    // 									: null,
    // 								uid: doc.id
    // 							};
    // 							// Store the received data about the user in the Local Storage
    // 							localStorage.setItem("userDtls", JSON.stringify(userDtls));
    //
    // 							// Also dispatch the details to Vuex store
    // 							store.dispatch("updateUserDetails", userDtls);
    // 						}
    //
    // 						// Check if the userType is normal then proceed to the user routes else redurect the user to the admin dashboard
    // 						if (userDtls.userType === "normal") {
    // 							next();
    // 						} else {
    // 							next({name: "adminDashboard"});
    // 						}
    // 					});
    // 			}
    // 		}
    // 		store.dispatch("plan/fetchPlans")
    // 	} else {
    // 		// User is not sugnin in, redirect to the login page
    // 		next({name: "login"});
    // 	}
    // } else if (to.matched.some(rec => rec.meta.requiresNotAuth)) {
    // 	// Check auth state of the user
    // 	if (auth.currentUser) {
    // 		// Get the details about the loggedin user from the local storage
    // 		let userDtls = null;
    // 		if (localStorage.getItem("userDtls")) {
    // 			userDtls = JSON.parse(localStorage.getItem("userDtls"));
    // 			// Check if the userType is admin then proceed to the admin routes else redurect the user to the user dashboard
    // 			if (userDtls.userType === "admin" || userDtls.userType === "manager") {
    // 				next({name: "adminDashboard"});
    // 			} else {
    // 				next({name: "userDashboard"});
    // 			}
    // 		} else {
    // 			db.collection("users")
    // 				.doc(auth.currentUser.uid)
    // 				.get()
    // 				.then(doc => {
    // 					// Check if the document exists - then do more checks; Else throw error
    // 					if (doc.exists) {
    // 						userDtls = {
    // 							firstName: doc.data().firstName,
    // 							lastName: doc.data().lastName,
    // 							email: doc.data().email,
    // 							userType: doc.data().userType,
    // 							customerID: doc.data().customerID
    // 								? doc.data().customerID
    // 								: null,
    // 							uid: doc.id
    // 						};
    // 						// Store the received data about the user in the Local Storage
    // 						localStorage.setItem("userDtls", JSON.stringify(userDtls));
    //
    // 						// Also dispatch the details to Vuex store
    // 						store.dispatch("updateUserDetails", userDtls);
    // 					}
    //
    // 					// Check if the userType is admin then proceed to the admin routes else redurect the user to the user dashboard
    // 					if (userDtls.userType === "admin" || userDtls.userType === "manager") {
    // 						next({name: "adminDashboard"});
    // 					} else {
    // 						next({name: "userDashboard"});
    // 					}
    // 				});
    // 		}
    // 	} else {
    // 		next();
    // 	}
    // } else {
    // 	next();
    // }
});

export default router;
